/**
 * SurfacePage.tsx — one product surface, assembled from what the Engine published.
 *
 * A surface is not a screen this repository designed. It is: a list of things
 * somebody needs, each one resolved against the Engine's own manifest, and then
 * run through the SAME generic loop every other capability in the workbench uses.
 * There is no per-discipline renderer here, no per-discipline form, and no
 * per-discipline validation message, because the descriptor is what varies and the
 * interaction is what does not.
 *
 * WHAT MAKES IT HONEST RATHER THAN DECORATIVE
 * -------------------------------------------
 *   • EVERY slot appears. A slot the Engine does not publish is rendered by name,
 *     with the missing publication stated, in the same list as the served ones.
 *     There is no path here that drops a slot, so the page can claim it has not
 *     quietly shown less than it was asked for — and `surfaceAvailability.test.ts`
 *     asserts that claim per slot rather than in total.
 *   • A served slot that this browser cannot ADDRESS shows the Engine's own
 *     missing publication, verbatim, rather than a control that would fail.
 *   • A served slot whose declared role disagrees with what the Engine published
 *     says so, instead of offering the wrong button.
 *   • Reads are displayed through `CurrentnessPanel`, which repeats the Engine's
 *     status and interprets nothing. A read that comes back `stale`, `partial`,
 *     `insufficient_data`, `unsupported`, or a status the Engine's own schema does
 *     not enumerate, is shown as exactly that.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * It computes nothing. No line list is assembled from geometry, no duct fill is
 * derived from a length, no voltage drop is produced from a current. Every value
 * on this page is a value the Engine returned, under the Engine's own field name.
 */

import { useCallback, useMemo, useState } from "react";
import { useClient } from "../../api/ClientContext";
import { buildReadRequest } from "../../api/capabilityAction";
import type { CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { BasisPanel } from "../basis/BasisRetrieval";
import { CapabilityWorkbench } from "../capabilities/CapabilityWorkbench";
import { ValueTree } from "../capabilities/ValueTree";
import { readCurrentness, currentnessStatement, type CurrentnessReading } from "./currentness";
import { resolveSurface, type AbsentSlot, type ResolvedSurface, type ServedSlot, type SurfaceDefinition } from "./surfaceSlots";

export function SurfacePage({ manifest, projectId, surface }: {
  manifest: CapabilityManifest;
  projectId: string;
  surface: SurfaceDefinition;
}): JSX.Element {
  const resolved = useMemo(() => resolveSurface(manifest, surface), [manifest, surface]);
  /**
   * The surface opens on its FIRST DECLARED slot, served or not.
   *
   * Choosing the first SERVED slot instead would open a surface on whatever happens
   * to be available, and quietly lead with the wrong thing. On a surface whose
   * first declaration is a safety determination, that would mean opening on some
   * other capability the Engine does publish and burying the determination — which
   * is the reason somebody opened the surface, and which the Engine does not publish
   * at all. A surface has to be able to open by saying what it cannot show.
   */
  const [selected, setSelected] = useState<string | undefined>(surface.slots[0]?.key);
  const active = resolved.served.find(entry => entry.slot.key === selected);
  const absent = resolved.absent.find(entry => entry.slot.key === selected);

  return (
    <div className="page">
      <header className="page-head">
        <h1>{surface.title}</h1>
        <p className="muted">{surface.purpose}</p>
        <p className="muted" data-stage="surface-tally">
          {resolved.served.length} of {surface.slots.length} served by the Engine&apos;s manifest ·{" "}
          {resolved.absent.length} named as not published
        </p>
      </header>

      <div className="capability-layout">
        <aside className="capability-catalogue">
          <SlotList resolved={resolved} selected={selected} onSelect={setSelected} />
        </aside>
        <main className="capability-main">
          {active !== undefined
            ? <ServedSlotPanel manifest={manifest} projectId={projectId} entry={active} />
            : absent !== undefined
              ? <AbsentSlotPanel absent={absent} />
              : <Panel title="Surface"><Empty>Choose what you need from this surface.</Empty></Panel>}
        </main>
      </div>
    </div>
  );
}

/**
 * The slot list, with the absences IN the list rather than below it.
 *
 * They are interleaved with the served slots in declaration order on purpose. A
 * separate "not available" section is easier to build and easier to miss; a user
 * scrolling the surface they were sent to should see the gap where it belongs.
 */
function SlotList({ resolved, selected, onSelect }: {
  resolved: ResolvedSurface;
  selected: string | undefined;
  onSelect: (key: string) => void;
}): JSX.Element {
  return (
    <Panel title="What this surface needs">
      <ul className="capability-list">
        {resolved.definition.slots.map(slot => {
          const served = resolved.served.find(entry => entry.slot.key === slot.key);
          return (
            <li key={slot.key}>
              <button
                type="button"
                data-slot={slot.key}
                className={slot.key === selected ? "is-armed" : undefined}
                onClick={() => onSelect(slot.key)}
              >
                <span className="capability-id">{slot.purpose}</span>
                <span className="muted">
                  {served !== undefined
                    ? <code>{served.descriptor.id}</code>
                    : <span className="slot-absent" data-absent="true">not published: <code>{slot.capabilityId}</code></span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

/** A slot the Engine does not publish, named. */
export function AbsentSlotPanel({ absent }: { absent: AbsentSlot }): JSX.Element {
  return (
    <Panel title={absent.slot.purpose} tone="warn" testId="slot-absent">
      <p className="bad">
        <strong>This surface cannot show this.</strong> The Engine&apos;s manifest publishes no capability{" "}
        <code data-stage="absent-capability">{absent.slot.capabilityId}</code>.
      </p>
      <dl className="facts">
        <dt>Needed for</dt><dd>{absent.slot.purpose}</dd>
        <dt>Missing</dt><dd>{absent.missing}</dd>
        <dt>Role</dt><dd>{absent.slot.role}</dd>
      </dl>
      <p className="muted">{absent.detail}</p>
      <p className="muted">
        Nothing is shown in its place. A value invented here would be engineering this browser is not entitled to
        produce, and a blank panel would be indistinguishable from an empty plant.
      </p>
    </Panel>
  );
}

/** A served slot: either the read surface or the full generic action loop. */
function ServedSlotPanel({ manifest, projectId, entry }: {
  manifest: CapabilityManifest;
  projectId: string;
  entry: ServedSlot;
}): JSX.Element {
  if (!entry.resolution.addressable) {
    const { unavailable } = entry.resolution.unaddressable;
    return (
      <Panel title={entry.slot.purpose} tone="warn" testId="slot-unaddressable">
        <p className="bad"><strong>Published by the Engine, not addressable from this browser.</strong></p>
        <dl className="facts">
          <dt>Capability</dt><dd><code>{entry.descriptor.id}</code></dd>
          <dt>Transport</dt><dd><code>{unavailable.transport}</code></dd>
          <dt>Missing</dt><dd>{unavailable.missing}</dd>
        </dl>
        <p className="muted">{unavailable.detail}</p>
        <p className="muted">{entry.descriptor.presentation.summary}</p>
      </Panel>
    );
  }
  const plan = entry.resolution.plan;
  if (!plan.phases.includes("read")) {
    // A mutation runs the full seven-stage loop. It is the same component every
    // capability in the workbench uses, with no panel-specific behaviour at all.
    return (
      <>
        {!entry.roleAgrees && <RoleMismatch entry={entry} />}
        <CapabilityWorkbench projectId={projectId} manifest={manifest} capabilityId={entry.descriptor.id} />
      </>
    );
  }
  return (
    <>
      {!entry.roleAgrees && <RoleMismatch entry={entry} />}
      <ReadSlot manifest={manifest} projectId={projectId} entry={entry} />
    </>
  );
}

function RoleMismatch({ entry }: { entry: ServedSlot }): JSX.Element {
  return (
    <p className="bad" data-stage="slot-role-mismatch">
      <strong>This surface asked for the wrong thing.</strong> The slot wants a {entry.slot.role}, and the Engine
      publishes <code>{entry.descriptor.id}</code> as a {entry.descriptor.kind}. The slot table in this repository is
      wrong, not the Engine&apos;s manifest.
    </p>
  );
}

/**
 * A read, displayed.
 *
 * The whole point of this component is what it does NOT do: it does not turn
 * `insufficient_data` into "no data", it does not treat an unrecognised status as
 * a benign one, and it does not flatten the Engine's issues. Every one of those
 * would be a browser deciding what an Engine's answer means, which is the one
 * authority this repository refuses to hold.
 */
function ReadSlot({ manifest, projectId, entry }: {
  manifest: CapabilityManifest;
  projectId: string;
  entry: ServedSlot;
}): JSX.Element {
  const client = useClient();
  const plan = entry.resolution.addressable ? entry.resolution.plan : undefined;
  const read = useAsync(
    async () => (plan === undefined ? undefined : client.capabilityRead(projectId, buildReadRequest(plan, {}).body)),
    [client, plan, projectId],
    { enabled: plan !== undefined },
  );
  const run = useCallback(() => read.reload(), [read]);
  const currentness = useMemo<CurrentnessReading | undefined>(
    () => (read.data === undefined ? undefined : readCurrentness(entry.descriptor, read.data)),
    [entry.descriptor, read.data],
  );

  return (
    <div className="surface-read">
      {!entry.roleAgrees && <RoleMismatch entry={entry} />}
      <Panel
        title={entry.slot.purpose}
        testId="stage-read"
        actions={
          <button type="button" onClick={run} disabled={read.loading}>
            {read.loading ? "Reading…" : "Read from the Engine"}
          </button>
        }
      >
        <p className="muted">{entry.descriptor.presentation.summary}</p>
        <dl className="facts compact">
          <dt>Capability</dt><dd><code>{entry.descriptor.id}</code></dd>
          <dt>Addressed as</dt><dd><code data-stage="read-address">{plan?.address}</code></dd>
          <dt>Descriptor digest</dt><dd className="request-id">{entry.descriptor.digest}</dd>
        </dl>
        <ErrorBanner error={read.error} onDismiss={read.reload} />
        {currentness !== undefined && <CurrentnessPanel reading={currentness} />}
        {read.data !== undefined && (
          <>
            <h3>Returned by the Engine, verbatim</h3>
            <ValueTree
              value={read.data}
              schema={entry.descriptor.resultSchema}
              highlight={entry.descriptor.presentation.primaryFields}
            />
          </>
        )}
      </Panel>
      <BasisPanel manifest={manifest} descriptor={entry.descriptor} projectId={projectId} />
    </div>
  );
}

/**
 * The Engine's own words about how current its answer is.
 *
 * `settled` controls a class name and nothing else. A `published` status is drawn
 * neutrally even when the status reads "ok", because the browser has not
 * established currency — the Engine stated a status, and this is a statement about
 * that, not about the plant.
 */
function CurrentnessPanel({ reading }: { reading: CurrentnessReading }): JSX.Element {
  const statement = currentnessStatement(reading);
  return (
    <div
      className="currentness"
      data-stage="stage-currentness"
      data-status-reading={reading.status}
      data-status={reading.statusValue ?? ""}
    >
      <p className={statement.settled ? "muted" : "bad"}>{statement.text}</p>
      {reading.issues.length > 0 && (
        <>
          <h3>The Engine&apos;s own issues</h3>
          <ul className="findings" data-stage="read-issues">
            {reading.issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className={`severity-${issue.severity}`}>
                <code>{issue.code}</code>
                <span className="muted">{issue.severity}</span>
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {reading.assumptions.length > 0 && (
        <>
          <h3>The assumptions the Engine states it is resting on</h3>
          <ul data-stage="read-assumptions">
            {reading.assumptions.map((text, index) => <li key={index}>{text}</li>)}
          </ul>
        </>
      )}
      {reading.freshness !== undefined && (
        <>
          <h3>Freshness, as the Engine reports it</h3>
          <ValueTree value={reading.freshness} schema={undefined} highlight={[]} />
        </>
      )}
    </div>
  );
}
